import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import type { ThemeMode } from '@/context/ThemeContext';

const OPTIONS: { mode: ThemeMode; icon: string; label: string }[] = [
  { mode: 'light', icon: 'ri-sun-line', label: 'Claro' },
  { mode: 'dark', icon: 'ri-moon-line', label: 'Oscuro' },
  { mode: 'system', icon: 'ri-computer-line', label: 'Sistema' },
];

interface ThemeToggleProps {
  direction?: 'up' | 'down';
  showLabel?: boolean;
}

export default function ThemeToggle({ direction = 'down', showLabel = false }: ThemeToggleProps) {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const current = OPTIONS.find((o) => o.mode === theme) ?? OPTIONS[2];
  const icon = resolvedTheme === 'dark' ? 'ri-moon-line' : 'ri-sun-line';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 transition whitespace-nowrap cursor-pointer ${
          showLabel ? 'w-full justify-between' : ''
        }`}
        title="Modo de apariencia"
      >
        <span className="flex items-center gap-2">
          <i className={`${icon} text-base`} />
          {showLabel && <span>{current.label}</span>}
        </span>
        {showLabel && <i className="ri-arrow-up-s-line text-sm text-foreground-400" />}
      </button>

      {open && (
        <div
          className={`absolute right-0 z-50 w-44 rounded-md border border-background-200 bg-background-50 p-1 ${
            direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          {OPTIONS.map((o) => (
            <button
              key={o.mode}
              onClick={() => {
                setTheme(o.mode);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition cursor-pointer ${
                theme === o.mode
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <i className={`${o.icon} text-base`} />
              {o.label}
              {theme === o.mode && <i className="ri-check-line ml-auto" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}